"use client";

import MultipleSelector, { type Option } from "@/components/ui/multiselect";
import { Label } from "@/components/ui/label";
import { useMemo, useState } from "react";

/**
 * Attendee picker for booking dialogs (issue #12, task 3.5). Workspace
 * members are offered as options; free-typed addresses are allowed
 * (creatable). Emits a hidden `attendees` JSON field so the existing
 * (prevState, formData) server-action signatures stay untouched.
 *
 * Only rendered when the resource has an active room link — invites are
 * delivered by Outlook, so without sync they would silently go nowhere.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type AttendeeMemberOption = { email: string; name: string };

export default function AttendeePicker({ members }: { members: AttendeeMemberOption[] }) {
  const [selected, setSelected] = useState<Option[]>([]);

  const options = useMemo<Option[]>(
    () =>
      members.map((m) => ({
        value: m.email.toLowerCase(),
        label: m.name ? `${m.name} (${m.email})` : m.email,
        group: "Workspace members",
      })),
    [members],
  );

  const attendees = useMemo(
    () =>
      selected
        .map((option) => {
          const email = option.value.trim().toLowerCase();
          const member = members.find((m) => m.email.toLowerCase() === email);
          return { email, name: member?.name ?? null };
        })
        .filter((a) => EMAIL_RE.test(a.email)),
    [selected, members],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label>
        Invite attendees <span className="text-slate-70 font-normal">(optional)</span>
      </Label>
      <MultipleSelector
        value={selected}
        onChange={setSelected}
        options={options}
        groupBy="group"
        creatable
        placeholder="Add people by name or email…"
        hidePlaceholderWhenSelected
        emptyIndicator={<p className="text-center text-sm text-slate-70">Type an email address</p>}
      />
      <input type="hidden" name="attendees" value={JSON.stringify(attendees)} />
    </div>
  );
}
